// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSearchDataModeActive } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection/AccordionSection";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    GridItem,
    Typography,
} from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { DataTablePresentationProps } from "@tps/MobiusOverrides/DataTable/DataTable";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    searchDataModeActive: getSearchDataModeActive(state),
    breadcrumbLinks: state.components.breadcrumbs.links,
    searchWithinQueries: state.pages.productList.productFilters.searchWithinQueries,
});

type Props = ReturnType<typeof mapStateToProps> & HasProduct;

export interface ProductSearchScoreDataStyles {
    container?: GridContainerProps;
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    gridItem?: GridItemProps;
    multilineGridItem?: GridItemProps;
    dataNameTypography?: TypographyPresentationProps;
    dataValueTypography?: TypographyPresentationProps;
    viewMoreLink?: LinkPresentationProps;
    // XNG-Changes
    dataTable?: DataTablePresentationProps;
    cellLabel?: DataTableCellProps;
    aggregateFieldScoresWrapper?: InjectableCss;
}

export const productSearchScoreDataStyles: ProductSearchScoreDataStyles = {
    container: {
        gap: 0,
        css: css`
            flex-direction: column;
        `,
    },
    gridItem: {
        width: 12,
    },
    multilineGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    dataNameTypography: {
        weight: "bold",
        css: css`
            padding-right: 5px;
            word-wrap: normal;
        `,
    },
    viewMoreLink: {
        css: css`
            margin-top: 5px;
        `,
    },
};

const styles = productSearchScoreDataStyles;

const ProductSearchScoreData: FC<Props> = ({
    searchDataModeActive,
    breadcrumbLinks,
    searchWithinQueries,
    product,
    product: { score, scoreExplanation },
}) => {
    const [extraData, toggleExtraData] = React.useState<boolean>(false);

    const containsSearchResults = (breadcrumbLinks || []).some(link => {
        return (
            link.children &&
            typeof link.children === "string" &&
            link.children.trim().toLowerCase().startsWith("search results")
        );
    });

    if (
        (!searchDataModeActive || (score === 0 && !containsSearchResults)) &&
        (!searchDataModeActive || (score === 0 && searchWithinQueries === undefined))
    ) {
        return null;
    }

    // XNG-Changes: styling
    return (
        <Accordion {...styles.accordion} headingLevel={3}>
            <ManagedAccordionSection
                title={translate("Search Data")}
                initialExpanded={true}
                {...styles.accordionSection}
            >
                <DataTable {...styles.dataTable} {...{ isScrollable: false }}>
                    <DataTableHead>
                        <DataTableHeader></DataTableHeader>
                        <DataTableHeader></DataTableHeader>
                    </DataTableHead>
                    <DataTableBody>
                        <DataTableRow>
                            <DataTableCell {...styles.cellLabel}>
                                <Typography title={translate("Score")}>{translate("Score")}:</Typography>
                            </DataTableCell>
                            <DataTableCell>{score}</DataTableCell>
                        </DataTableRow>

                        {scoreExplanation && (
                            <DataTableRow>
                                <DataTableCell {...styles.cellLabel}>
                                    <Typography title={translate("Matching Fields")}>
                                        {translate("Matching Fields")}:
                                    </Typography>
                                </DataTableCell>
                                <DataTableCell>
                                    {scoreExplanation?.aggregateFieldScores?.map(s => s.name).join(", ")}
                                </DataTableCell>
                            </DataTableRow>
                        )}
                        {extraData && scoreExplanation && (
                            <>
                                <DataTableRow>
                                    <DataTableCell {...styles.cellLabel}>
                                        <Typography title={translate("Total Boost")}>
                                            {translate("Total Boost")}:
                                        </Typography>
                                    </DataTableCell>
                                    <DataTableCell>{scoreExplanation?.totalBoost}</DataTableCell>
                                </DataTableRow>
                                {product.isSponsored && (
                                    <DataTableRow>
                                        <DataTableCell colSpan={2} {...styles.cellLabel}>
                                            <Typography>{translate("Sponsored Boost Active")}</Typography>
                                        </DataTableCell>
                                    </DataTableRow>
                                )}
                                <DataTableRow>
                                    <DataTableCell {...styles.cellLabel}>
                                        <Typography title={translate("Field Scores")}>
                                            {translate("Field Scores")}:
                                        </Typography>
                                    </DataTableCell>
                                    <DataTableCell>
                                        {scoreExplanation?.aggregateFieldScores
                                            ?.filter(s => s.score !== 0)
                                            .map(s => (
                                                <StyledWrapper key={s.name} {...styles.aggregateFieldScoresWrapper}>
                                                    <Typography {...styles.dataValueTypography}>
                                                        {s.name}: {s.score}
                                                    </Typography>
                                                </StyledWrapper>
                                            ))}
                                    </DataTableCell>
                                </DataTableRow>
                            </>
                        )}
                    </DataTableBody>
                </DataTable>
                {scoreExplanation && (
                    <Link {...styles.viewMoreLink} onClick={() => toggleExtraData(!extraData)}>
                        {extraData ? "View Less" : "View More"}
                    </Link>
                )}
                {/* <GridContainer {...styles.container}>
                    <GridItem {...styles.gridItem}>
                        <Typography {...styles.dataNameTypography}>{translate("Score")}:</Typography>
                        <Typography {...styles.dataValueTypography}>{score}</Typography>
                    </GridItem>
                    {scoreExplanation && (
                        <GridItem {...styles.gridItem}>
                            <Typography {...styles.dataNameTypography}>{translate("Matching Fields")}: </Typography>
                            <Typography {...styles.dataValueTypography}>
                                {scoreExplanation?.aggregateFieldScores?.map(s => s.name).join(", ")}
                            </Typography>
                        </GridItem>
                    )}
                    {extraData && scoreExplanation && (
                        <>
                            <GridItem {...styles.gridItem}>
                                <Typography {...styles.dataNameTypography}>{translate("Total Boost")}:</Typography>
                                <Typography {...styles.dataValueTypography}>{scoreExplanation?.totalBoost}</Typography>
                            </GridItem>
                            {product.isSponsored && (
                                <GridItem {...styles.gridItem}>
                                    <Typography {...styles.dataNameTypography}>
                                        {translate("Sponsored Boost Active")}
                                    </Typography>
                                </GridItem>
                            )}
                            <GridItem {...styles.multilineGridItem}>
                                <Typography {...styles.dataNameTypography}>{translate("Field Scores")}: </Typography>
                                {scoreExplanation?.aggregateFieldScores
                                    ?.filter(s => s.score !== 0)
                                    .map(s => (
                                        <Typography key={s.name} {...styles.dataValueTypography}>
                                            {s.name}: {s.score}
                                        </Typography>
                                    ))}
                            </GridItem>
                        </>
                    )}
                    {scoreExplanation && (
                        <GridItem {...styles.gridItem}>
                            <Link {...styles.viewMoreLink} onClick={() => toggleExtraData(!extraData)}>
                                {extraData ? "View Less" : "View More"}
                            </Link>
                        </GridItem>
                    )}
                </GridContainer> */}
            </ManagedAccordionSection>
        </Accordion>
    );
};

export default connect(mapStateToProps)(withProduct(ProductSearchScoreData));
